import { Box, Divider, Skeleton, Stack, Typography } from "@mui/material";
import { times } from "lodash";
import { useRouter } from "next/router";
import { Fragment } from "react";
import { FeaturePermission } from "@/__generated__/featurePermissions";
import { hasRole } from "@/auth/helpers";
import { useUser } from "@/auth/useUser";
import OutlinedButton from "@/components/common/buttons/outlinedButton";
import InfoCard from "@/components/common/infoCard/infoCard";
import MembershipClientCard from "@/components/memberships/clientDetails/membershipClientCard";
import { useClientAccessToken } from "@/contexts/clientAccessTokenContext";
import { useClientBasicInfoQuery } from "@/graphql/queries/client/basicInfo.graphql.types";
import { useClientMembershipsQuery } from "@/graphql/queries/clientMemberships.graphql.types";
import useWallet from "@/hooks/clients/useWallet";
import useCreateClientMembershipStore, {
  CreateClientMembershipStep,
  getSetSelectedClient,
  getSetStep,
} from "@/store/views/serviceFlow/memberships/createClientMembershipStore";
import { MEDICAL_DIRECTOR } from "@/types";
import useErrorLogger from "@/utils/useErrorLogger";
import EmptySection from "./emptySection";
import ExpandableSection from "./expandableSection";

type Props = {
  medspaName?: string;
  clientId?: string;
  isClientUser?: boolean;
};

export default function MembershipsSection({
  medspaName = "",
  clientId: clientIdFromProps,
  isClientUser = false,
}: Props) {
  const logError = useErrorLogger();
  const { medspa, user, hasFeaturePermission, newPermissionsEnabledForUser } =
    useUser();

  const isMdOrGfe = hasRole(user, [MEDICAL_DIRECTOR]);
  const canViewClientMemberships = newPermissionsEnabledForUser
    ? hasFeaturePermission(FeaturePermission.VIEW_CLIENT_MEMBERSHIP)
    : !isMdOrGfe;
  const canCreateClientMembership =
    hasFeaturePermission &&
    hasFeaturePermission(FeaturePermission.CREATE_CLIENT_MEMBERSHIP);

  const setSelectedClient =
    useCreateClientMembershipStore(getSetSelectedClient);
  const setStep = useCreateClientMembershipStore(getSetStep);

  const {
    query: { slug },
    push,
  } = useRouter();

  const { clientAccessToken: token, getTokenContext } = useClientAccessToken();

  const clientIdFromSlug = slug as string;

  const { defaultCard, clientId } = useWallet(
    clientIdFromProps || clientIdFromSlug
  );

  const { data: clientData, error: clientDataError } = useClientBasicInfoQuery({
    variables: {
      clientId,
    },
    skip: !!token || !clientId,
    onError: (error) => {
      logError(error, "Error fetching client data");
    },
  });

  const { data, error } = useClientMembershipsQuery({
    variables: {
      clientId,
    },
    skip: !clientId || !canViewClientMemberships,
    fetchPolicy: "cache-and-network",
    ...getTokenContext(),
    onError: (error) => {
      logError(error, "Error fetching client membership data");
    },
  });

  const clientMemberships = data?.clientMembership;
  const isLoading = !data && !error;

  if (!error && clientMemberships?.length === 0 && isClientUser) {
    return null;
  }

  const handleAddNewClientMembership = () => {
    setSelectedClient(clientData.clientByPk);
    setStep(CreateClientMembershipStep.MEMBERSHIP_SELECTION);
    push(`/${medspa}/client-memberships/new`);
  };

  return (
    <InfoCard>
      <ExpandableSection title="Memberships" expanded>
        {isLoading && <LoadingState />}
        {!canViewClientMemberships &&
          "You don't have permissions to view this."}
        {!isLoading && canViewClientMemberships && error && (
          <ErrorMessage
            title="We are unable to load membership details."
            description={
              isClientUser
                ? `If you do have a membership at ${medspaName}, please contact us.`
                : `Error fetching client membership data: ${error}`
            }
          />
        )}
        {!isLoading && !error && canViewClientMemberships && (
          <>
            {clientMemberships?.length ? (
              <Stack sx={{ mt: 3 }}>
                {clientMemberships?.map((membership, index) => {
                  return (
                    <Fragment key={membership.id}>
                      {index > 0 && <Divider sx={{ mb: 2 }} />}
                      <MembershipClientCard
                        membership={membership}
                        clientId={clientId}
                        defaultCard={defaultCard}
                        isClientUser={isClientUser}
                      />
                    </Fragment>
                  );
                })}
              </Stack>
            ) : (
              <EmptySection>No memberships yet</EmptySection>
            )}
          </>
        )}
        {!clientDataError && !isClientUser && canCreateClientMembership && (
          <OutlinedButton
            onClick={handleAddNewClientMembership}
            text="Add new membership"
            sx={{ mt: 3, mb: 0 }}
          />
        )}
      </ExpandableSection>
    </InfoCard>
  );
}

const LoadingState = () => (
  <>
    {times(4, (idx: number) => (
      <Skeleton
        key={`skeleton-${idx}`}
        variant="rectangular"
        sx={{ mb: 1.5, height: 180 }}
      />
    ))}
  </>
);

const ErrorMessage = ({ title, description }) => {
  return (
    <Box
      sx={{
        pt: 3,
        pb: 1,
      }}
    >
      <Typography
        variant="body1"
        sx={{
          textAlign: "center",
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="labelTiny"
        sx={{
          textAlign: "center",
        }}
      >
        {description}
      </Typography>
    </Box>
  );
};
